// Page content as supplied in the REGERA Website Content & Presentation Specification.

export const HOME = {
  opening: {
    overline: "Technology · Solutions · Platforms · Advisory",
    title: "Technology that moves business forward.",
    body: "REGERA brings technology, business solutions and leadership together to help enterprises and public-sector organisations solve problems, build capabilities and turn ideas into working solutions.",
  },
  does: {
    overline: "What REGERA does",
    title: "From business needs to technology solutions.",
    body: "We help organisations identify opportunities, make informed technology decisions, build and implement solutions, and sustain them as business needs evolve.",
    pillars: [
      { title: "Consulting & Advisory", text: "Technology leadership, strategy, architecture and business advice." },
      { title: "Solutions & Platforms", text: "Digital platforms, applications, integration and business solutions." },
      { title: "Products & Innovation", text: "Ideas, prototypes, product development and commercialisation." },
      { title: "Technology Operations", text: "Technology management, delivery, support and ongoing improvement." },
    ],
  },
  services: { overline: "Services", title: "The capabilities to make it happen." },
  meta: {
    overline: "Metamorphosis",
    title: "From an idea to enterprise value.",
    body: "Every promising idea needs validation, a workable solution, a market and a path to scale. Metamorphosis is REGERA's journey from ideation and validation to product development, market entry and growth.",
  },
  experience: {
    overline: "Experience & engagement",
    title: "Technology must deliver value.",
    body: "Have a business challenge, technology requirement or idea to take forward? Let's discuss what you need and how REGERA can help.",
  },
};

export const ABOUT = {
  who: {
    overline: "Who we are",
    title: "Technology first. Business always.",
    body: "REGERA works at the intersection of technology, business needs and execution. We bring these elements together to help organisations make informed decisions, deliver practical solutions and build capabilities for the future.",
  },
  pov: {
    overline: "Our point of view",
    title: "Technology is a means. Business value is the purpose.",
    body: "Technology creates value when it solves a real problem, supports a clear objective and works in practice. Our approach connects the business requirement with the right solution and the ability to deliver it.",
  },
  how: {
    overline: "How we work",
    title: "Five ideas shape our work.",
    ideas: [
      { title: "Change", text: "Respond to changing needs and opportunities." },
      { title: "Kaizen", text: "Pursue continuous, practical improvement." },
      { title: "Governance", text: "Build accountability, control and trust into delivery." },
      { title: "Transformation", text: "Turn plans and ideas into meaningful change." },
      { title: "Digital Excellence", text: "Use technology purposefully to improve capability and performance." },
    ],
  },
  leadership: {
    overline: "Leadership",
    title: "Experienced leadership. Practical execution.",
    body: "REGERA's approach connects business understanding, technology expertise and delivery responsibility. Advice must be actionable, decisions must be informed, and solutions must work in the real world.",
  },
};

// slug = existing service anchor; area = contact form "Area of interest" option
export const SERVICE_GROUPS = [
  {
    id: "digital-product",
    title: "Digital & Product Capabilities",
    services: [
      { n: "01", slug: "digital-transformation", area: "Digital Platforms", title: "Digital Platforms & Transformation",
        text: "Design and implement technology solutions that connect business processes, people, data and systems.",
        points: ["Digital platforms and enterprise applications", "Cloud solutions and migration", "Systems integration and automation", "Data flows and business intelligence", "Digital process improvement", "Implementation and adoption"] },
      { n: "02", slug: "product-solution-consulting", area: "Products & Solutions", title: "Products & Solutions",
        text: "Turn business needs and promising ideas into usable products, applications and solutions.",
        points: ["Product concepts and requirements", "Prototypes and proof of concept", "Software and platform development", "Testing and quality assurance", "Product launch and commercialisation", "Product lifecycle improvement"] },
      { n: "03", slug: "technology-advisory", area: "Technology Advisory", title: "Technology Advisory",
        text: "Make informed technology decisions aligned with business needs, risk, investment and future growth.",
        points: ["Technology strategy and roadmaps", "Enterprise and solution architecture", "Cloud, data and AI decisions", "Cybersecurity and resilience", "Technology and vendor evaluation", "IT governance and investment priorities"] },
    ],
  },
  {
    id: "leadership-transformation",
    title: "Leadership & Transformation",
    services: [
      { n: "04", slug: "virtual-cio", area: "Virtual CIO", title: "Virtual CIO / Technology Leadership",
        text: "Access experienced technology leadership without necessarily maintaining a full-time CIO position.",
        points: ["Technology direction and roadmaps", "IT budgets and investment priorities", "Architecture and technology decisions", "Vendor and service-provider management", "IT risk, security and governance", "Technology programmes and performance"] },
      { n: "05", slug: "strategy-management-consulting", area: "Business Consulting", title: "Strategy & Business Consulting",
        text: "Connect business objectives with practical plans, improved operations and informed decisions.",
        points: ["Business strategy and operating models", "Business process analysis", "Feasibility and business cases", "Digital business opportunities", "Performance improvement", "Implementation planning"] },
      { n: "06", slug: "business-transformation", area: "Transformation", title: "Business Transformation",
        text: "Help organisations translate change objectives into coordinated initiatives and measurable operational improvements.",
        points: ["Transformation roadmaps", "Process redesign and automation", "Programme coordination", "Organisational change and adoption", "Performance measures and reporting", "Benefits tracking"] },
    ],
  },
  {
    id: "governance-delivery",
    title: "Governance & Delivery",
    services: [
      { n: "07", slug: "governance-risk-compliance", area: "Governance, Risk & Compliance", title: "Governance, Risk & Compliance",
        text: "Establish the governance, controls and risk practices needed to operate responsibly and reliably.",
        points: ["Governance frameworks and accountability", "Risk identification and assessment", "Policies, processes and controls", "Cybersecurity and data-protection governance", "Compliance coordination and audit readiness", "Business continuity and resilience"] },
      { n: "08", slug: "startup-venture-advisory", area: "Startup & Venture Advisory", title: "Startup & Venture Advisory",
        text: "Help founders and ventures assess opportunities, validate ideas and prepare solutions for the market.",
        points: ["Business and product concepts", "Market and customer validation", "Product feasibility and technology roadmaps", "Prototypes and proof of concept", "Go-to-market planning", "Partnerships and commercialisation"] },
      { n: "09", slug: "program-project-management", area: "Programme & Project Management", title: "Programme & Project Management",
        text: "Plan and coordinate complex initiatives from initiation through delivery and handover.",
        points: ["Project planning and milestones", "Programme governance and reporting", "Resource and budget coordination", "Vendor and stakeholder management", "Risk, issue and dependency management", "Delivery tracking and closure"] },
    ],
  },
];
export const ALL_SERVICES = SERVICE_GROUPS.flatMap((g) => g.services);

export const INDUSTRY_PAGE = {
  coverage: {
    overline: "Industry coverage",
    title: "Technology across industries.",
    body: "REGERA applies technology, solutions and delivery capabilities to the distinct needs of enterprises, public institutions and industry sectors.",
    groups: [
      { title: "Government & Regulated Services", items: ["Government and semi-government", "Banking, financial services and insurance", "Investment and equity", "Telecom and internet service providers", "IT-enabled services and business process outsourcing"] },
      { title: "Infrastructure & Industrial Enterprises", items: ["Engineering and construction", "EPC", "Manufacturing", "Oil and gas", "Energy and utilities", "Shipping", "Transport and logistics"] },
    ],
  },
  emerging: {
    overline: "Industry coverage",
    title: "Services, commerce & emerging needs.",
    items: ["Retail", "Trading", "Franchising", "Travel and hospitality", "Healthcare and pharmaceuticals", "Education and training", "Disaster management and security"],
    closing: "Every sector has different needs. The right solution starts with understanding the problem.",
  },
};

export const PORTFOLIO_PAGE = {
  overline: "Transformation",
  title: "Selected engagements.",
  body: "Explore REGERA's work across industries, technology and business challenges. Some engagement details may be confidential or subject to non-disclosure obligations.",
};

export const META_PAGE = {
  intro: {
    overline: "Metamorphosis",
    title: "From potential to enterprise value.",
    body: "An idea becomes valuable when it is tested, shaped into a workable solution and connected to a real market. Metamorphosis brings these stages together in a structured journey.",
  },
  steps: [
    { n: "01", title: "Ideation", text: "Identify a problem, opportunity or idea worth pursuing." },
    { n: "02", title: "Due Diligence", text: "Examine feasibility, customer needs, available technology, constraints and commercial potential." },
    { n: "03", title: "Validation", text: "Test the assumptions and establish whether the proposed idea addresses a genuine need." },
    { n: "04", title: "Proof of Concept", text: "Demonstrate that the proposed solution can work in practice." },
    { n: "05", title: "Product", text: "Turn the validated concept into a usable, tested product or solution." },
    { n: "06", title: "Market", text: "Prepare the offering for launch, customers, distribution and commercial adoption." },
    { n: "07", title: "Scale", text: "Expand delivery, improve the offering and pursue sustainable growth." },
  ],
  engage: {
    overline: "Engagement",
    title: "Have an idea worth taking forward?",
    body: "REGERA can help assess the opportunity, validate the concept and identify the steps needed to turn it into a viable solution.",
  },
};

export const INNOVATION_PAGE = {
  pov: {
    overline: "Our point of view",
    title: "Technology is valuable when it solves a business problem.",
    body: "REGERA explores and applies technology to address practical needs, improve capabilities, create new solutions and enable new business opportunities.",
  },
  areas: {
    overline: "Technology areas",
    title: "Emerging technologies. Practical applications.",
    items: [
      { title: "Artificial Intelligence & Analytics", text: "Intelligent applications, data-driven insights and automation." },
      { title: "Cloud & Embedded Systems", text: "Scalable platforms and connected computing capabilities." },
      { title: "IoT & Robotics", text: "Connected devices, monitoring and automated operations." },
      { title: "Blockchain & Digital Assets", text: "Distributed systems and relevant digital applications." },
      { title: "Mobility & Wearables", text: "Connected experiences and mobile solutions." },
      { title: "Renewable Energy & Advanced Technologies", text: "Technology applications for evolving industry needs." },
    ],
  },
  apply: {
    overline: "Application",
    title: "Start with the problem. Select the technology that fits.",
    body: "Technology selection should follow the business need, feasibility, value and implementation requirements — not the popularity of a particular technology.",
  },
};

export const INSIGHTS_PAGE = {
  intro: {
    overline: "Insights",
    title: "Perspectives on technology and transformation.",
    body: "Explore ideas, lessons and practical perspectives on technology leadership, enterprise transformation, governance and emerging technologies.",
  },
  featured: {
    overline: "Featured perspectives",
    title: "Ideas that inform better decisions.",
    items: [
      { slug: "governance-as-the-foundation-of-sustainable-transformation", title: "Governance as the Foundation of Sustainable Transformation", text: "How governance, accountability and risk management support sustainable technology and business change." },
      { slug: "the-virtual-cio-senior-technology-leadership-on-demand", title: "The Virtual CIO: Senior Technology Leadership on Demand", text: "How organisations can access technology leadership, align IT with business objectives and manage technology investments." },
    ],
  },
};
